/**
 * NotificationDetailPage — 알림 상세보기 (Figma 1512:47401 이벤트 / 1512:47429 공지).
 *
 * 카테고리로 레이아웃 분기:
 *   - MARKETING → 이벤트 상세 ([이벤트] 태그, 이미지 영역(있을 때), 본문)
 *   - 그 외      → 공지 상세 ([공지] 태그, 본문)
 *
 * FE 전용 단계: 상세 전용 API 가 없어 알림 인박스(useNotificationsQuery) 캐시의
 * 항목(title/body/createdAt/category)으로 렌더한다. 이미지/조회수/기간 등 리치 필드는
 * BE 가 제공하면 표시되도록 optional 로 두되, 현재 데이터엔 없으므로 자연스럽게 생략된다.
 * (가짜 조회수/기간을 만들어내지 않는다 — 정확성 우선.)
 *
 * AppBar("알림 상세보기" + 우측 X)는 ActivityHost 가 popup variant 로 렌더한다.
 */
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { markNotificationRead, type NotificationItem } from "../../api/notification";
import {
  useNotificationsQuery,
  NOTIFICATIONS_KEY,
  NOTIFICATIONS_COUNT_KEY,
} from "../../hooks/useNotificationQueries";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { formatNotificationDate } from "./notificationMeta";
import styles from "./NotificationDetailPage.module.css";

export default function NotificationDetailPage() {
  const { t } = useTranslation("notification");
  const { id } = useParams<{ id: string }>();
  const serialNo = Number(id);
  const queryClient = useQueryClient();
  const { data, isLoading } = useNotificationsQuery();

  const item = data?.find((n) => n.notificationSerialNo === serialNo);

  // 진입 시 읽음 처리 (optimistic). 리스트에서 이미 읽음 처리됐어도 멱등.
  useEffect(() => {
    if (!item || item.readYn === "Y") return;
    const nextList = queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (prev) =>
      prev?.map((i) =>
        i.notificationSerialNo === item.notificationSerialNo ? { ...i, readYn: "Y" } : i,
      ) ?? prev,
    );
    // 뱃지는 유무(dot)만 → 갱신된 리스트로 남은 unread 존재 여부 재계산.
    if (nextList) {
      queryClient.setQueryData<boolean>(
        NOTIFICATIONS_COUNT_KEY,
        nextList.some((i) => i.readYn === "N"),
      );
    }
    void markNotificationRead(item.notificationSerialNo).catch(() => {
      // prefix 매칭 → 리스트 + 카운트 동시 재동기화 (optimistic 롤백)
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    });
  }, [item, queryClient]);

  if (isLoading && !item) {
    return (
      <div className={styles.page}>
        <Skeleton width="70%" height={24} />
        <Skeleton width="40%" height={14} />
        <Skeleton width="100%" height={200} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className={styles.notFound}>
        <div>{t("notFound")}</div>
      </div>
    );
  }

  // 이벤트(EVENT) → 이벤트 상세, 그 외(공지/문의답변 등) → 공지 상세.
  const isEvent = item.category === "EVENT";
  const tag = isEvent ? t("tag.event") : t("tag.system");

  return (
    <article className={styles.page}>
      <h1 className={styles.title}>
        <span className={styles.tag}>[{tag}]</span> {item.title}
      </h1>
      <div className={styles.meta}>
        <span>{formatNotificationDate(item.createdAt)}</span>
      </div>

      <div className={styles.divider} />

      <p className={styles.body}>{item.body}</p>
    </article>
  );
}
